import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { LoginService, PasswordRecoveryService } from '@ebw-monorepo/login';
import { ConfigSidebarService } from '@ebw-monorepo/map';
import { EbwModalService } from '@ebw-monorepo/modal';
import { PERMISSION_NAME, PermissionService } from '@ebw-monorepo/utils';
import { safeJsonStringify } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'apps/portale-saas/src/environments/environment';
import { PlotService } from 'libs/ng-components/map/src/lib/services/plot.service';
import { API_URLS, ApiUrlService } from '../../../../../../libs/ng-components/utils/src/lib/services/api-url.service';
import { AuthService } from '../../services/auth.service';
import { ConfigService } from '../../services/config.service';

@Component({
  selector: 'ebw-monorepo-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  public globalConfig;
  public logoImg: string | undefined;
  public bgImg: string | undefined;
  public hide = true;
  public loginError = '';

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _loginService: LoginService,
    private _authService: AuthService,
    private _ebwModalService: EbwModalService,
    private _configService: ConfigService,
    private _translateService: TranslateService,
    private _apiUrl: ApiUrlService,
    private _recoverySrv: PasswordRecoveryService,
    private configSidebar: ConfigSidebarService,
    private _plotService: PlotService,
    private _permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    this.globalConfig = this._configService.getConfigGlobale();
    this.setLogoAndBackground();

    const entraSession = this._route.snapshot.queryParams['entra_session'];
    if (entraSession) {
      this._handleEntraSession(entraSession);
    }
  }

  // ---------------------------------------------------------------------------

  isAuthEnabled(key: string): boolean {
    const authTypes = this.globalConfig?.auth_types;
    if (!authTypes) {
      return key === 'standard'; // config assente → solo standard
    }
    return authTypes.includes(key); // presenza nell'array → modalità supportata
  }

  get hasSsoLogin(): boolean {
    // estendibile a future modalità SSO (es. 'google')
    return this.isAuthEnabled('entraid');
  }

  // ---------------------------------------------------------------------------

  setLogoAndBackground() {
    // Controllo se siamo in Saas o onpremise
    const isSaas = environment.deployMode === 'saas';
    // Recupero il tenant
    let tenant = isSaas ? null : environment.customer;
    // Se non sono in saas ma non ho il tenant allora imposto un default
    if (!isSaas && !tenant) {
      tenant = 'default';
    }
    const basePathLogo = './assets/custom';
    const nomeLogoImg = 'logo_login.png';
    const nomeBackgroundImg = 'background_login.jpg';

    // imposto logo login
    this.logoImg = `${basePathLogo}/${nomeLogoImg}`;
    // imposto background login
    this.bgImg = `url(${basePathLogo}/${nomeBackgroundImg})`;
  }
  // ---------------------------------------------------------------------------

  async onLogin(loginForm: NgForm) {
    const username = loginForm.value.username;
    const password = loginForm.value.password;

    let formErrorMessage = '';
    if (username == '') {
      const userMandatoryMessage = this._translateService.instant('login.user_mandatory');
      formErrorMessage += userMandatoryMessage;
    }
    // client-side required-field check before submit; actual credential check happens server-side
    // nosemgrep: nodejs_scan.javascript-crypto-rule-node_timing_attack, eslint.detect-possible-timing-attacks
    if (password == '') {
      const passwordMandatoryMessage = this._translateService.instant('login.password_mandatory');
      formErrorMessage += passwordMandatoryMessage;
    }

    if (formErrorMessage.length > 0) {
      const errorTitle = this._translateService.instant('common.error');
      const errorMessage = this._translateService.instant('login.error_in_login');
      this._ebwModalService.openErrorModal({ title: errorTitle, content: `${errorMessage} ${formErrorMessage}` });
    } else {
      this._authService.login(username, password).subscribe({
        next: async (data: any) => {
          try {
            this._permissionService.decodePermissions(data.access_token);
            this._loginService.setToken(data.access_token);
            this._loginService.setUserId(data.user.id);
            this._loginService.setRefresh(data.refresh_token);
            this._loginService.setTenant(data.user.tenant_id);
            this._loginService.setTenantName(data.tenantName);
            this._loginService.setTokenExpireDate(data.expires);
            this._authService.automateRefresh(data.expires_in);
            this._authService.automateRefreshAlreadyTriggered$.next(true);

            const url_token = this._apiUrl.createUrl(API_URLS.AUTH.REFRESH_TOKEN);
            this._loginService.setApiUrlRefreshToken(url_token);

            this._configService.getAllConfigurations().subscribe({
              next: async () => {
                await this.sidebarConfig();

                // get the template list from the GSS
                if (!sessionStorage.getItem('templates') && this._permissionService.checkPermission(PERMISSION_NAME.MAP.PLOTTING)) {
                  this._plotService.getTemplatesPlotUrl().subscribe((result) => {
                    const payload = safeJsonStringify(result);
                    if (payload !== null) {
                      sessionStorage.setItem('templates', payload);
                    }
                  });
                }
                // Go to the main page (map)
                this._router.navigate(['home']);
              },

              error: async (error: any) => {
                this.clearTokens();
                //Error in retrieving configuration
                const errorTitle = this._translateService.instant('common.error');
                const errorMessage = this._translateService.instant('login.error_in_login_configuration');
                this._ebwModalService.openErrorModal({ title: errorTitle, content: errorMessage });
              }
            });
          } catch (error) {
            this.clearTokens();
            const errorTitle = this._translateService.instant('common.error');
            const errorMessage = this._translateService.instant('login.error_in_login_context');
            this._ebwModalService.openErrorModal({ title: errorTitle, content: errorMessage });
          }
        },

        error: (err) => {
          let errorMessage = '';
          if (err?.error?.message) {
            errorMessage = err.error.message;
          } else {
            if (err.status == 0) {
              errorMessage = this._translateService.instant('login.connection_error');
            } else {
              errorMessage = this._translateService.instant('login.internal_server_error');
            }
          }
          const errorTitle = this._translateService.instant('common.error');
          const errorLogin = this._translateService.instant('login.error_in_login');
          // dinamically create the error message
          let content = `${errorLogin}`;
          // if it's a forseen value, replace it with its translation
          if (err.status == 401) {
            content = `${this._translateService.instant('login.invalid_credentials')}`;
          } else {
            content += ` ${errorMessage}`;
          }
          // create the error modal with the specified title and content
          this._ebwModalService.openErrorModal({ title: errorTitle, content: content });
        }
      });
    }
  }

  loginWithEntra() {
    window.location.href = `${window.location.protocol}//${window.location.host}/auth/entra/login`;
  }

  private _handleEntraSession(sessionCode: string) {
    this._authService.entraExchangeSession(sessionCode).subscribe({
      next: async (data: any) => {
        try {
          this._permissionService.decodePermissions(data.access_token);
          this._loginService.setToken(data.access_token);
          this._loginService.setUserId(data.user.id);
          this._loginService.setRefresh(data.refresh_token);
          this._loginService.setTenant(data.user.tenant_id);
          this._loginService.setTenantName(data.tenantName);
          this._loginService.setTokenExpireDate(data.expires);
          this._loginService.setEntra('true');
          this._authService.automateRefresh(data.expires_in);
          this._authService.automateRefreshAlreadyTriggered$.next(true);

          const url_token = this._apiUrl.createUrl(API_URLS.AUTH.REFRESH_TOKEN);
          this._loginService.setApiUrlRefreshToken(url_token);

          this._configService.getAllConfigurations().subscribe({
            next: async () => {
              await this.sidebarConfig();

              if (!sessionStorage.getItem('templates') && this._permissionService.checkPermission(PERMISSION_NAME.MAP.PLOTTING)) {
                this._plotService.getTemplatesPlotUrl().subscribe((result) => {
                  sessionStorage.setItem('templates', JSON.stringify(result));
                });
              }
              this._router.navigate(['home']);
            },
            error: async () => {
              this.clearTokens();
              const errorTitle = this._translateService.instant('common.error');
              const errorMessage = this._translateService.instant('login.error_in_login_configuration');
              this._ebwModalService.openErrorModal({ title: errorTitle, content: errorMessage });
            }
          });
        } catch (error) {
          this.clearTokens();
          const errorTitle = this._translateService.instant('common.error');
          const errorMessage = this._translateService.instant('login.error_in_login_context');
          this._ebwModalService.openErrorModal({ title: errorTitle, content: errorMessage });
        }
      },
      error: () => {
        const errorTitle = this._translateService.instant('common.error');
        const errorMessage = this._translateService.instant('login.error_in_login');
        this._ebwModalService.openErrorModal({ title: errorTitle, content: errorMessage });
      }
    });
  }

  clearTokens() {
    this._loginService.clearToken();
  }

  passwordrecovery() {
    const options: MatDialogConfig = {
      width: '30%',
      data: {
        url: this._apiUrl.createUrl(API_URLS.AUTH.RECUPERA_PASSWORD)
      }
    };
    this._recoverySrv.openModal(options);
  }

  showHide(event: any) {
    event.preventDefault();
    // Prevent revealing the password when enter button is pressed.
    if (event?.pointerType) {
      this.hide = !this.hide;
    }
  }

  private async sidebarConfig() {
    // TODO filo: piccone (forse non la sidebar ma il resto va nel db o eliminato)
    this.configSidebar.setStyleConfiguration(this.globalConfig.styles);
    this.configSidebar.setExportSettings(this.globalConfig.export);
    this.configSidebar.setExternalSettings(this.globalConfig.external);
  }
}
